import {useState} from 'react';
import {GetStaticProps} from 'next';
import Head from 'next/head';
import Image, {StaticImageData} from 'next/image';
import {QueryClient, dehydrate} from '@tanstack/react-query';
import {Button, Container, Flex, Grid, Input, InputProps, Link, Text, useToast} from '@chakra-ui/react';

import {py} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {setApiLocale} from '@eksab/api';
import {useRegionSettings} from '@eksab/hooks';

import {fantasyScreenshot, tacticsScreenshot, triviaScreenshot} from '@eksab/features/partners/assets';
import {addUserIcon, handShakeIcon, keyIcon, moneyIcon, rocketIcon} from '@eksab/features/partners/assets/icons';
import {usePartnerSignupMutation} from '@eksab/features/partners/hooks';
import {SUPPORT_EMAIL} from '@eksab/const';

const initialFormData = {
  name: '',
  email: '',
  phone_number: '',
};

type FormField = keyof typeof initialFormData;

export default function Partners() {
  const toast = useToast();
  const [formData, setFormData] = useState(initialFormData);

  const t = useTranslate();
  const partnerSignupMutation = usePartnerSignupMutation();

  const onInputChange = (name: FormField) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({...prev, [name]: e.target.value}));

  const onSubmit = async (e: React.FormEvent<HTMLFormElement | HTMLDivElement>) => {
    e.preventDefault();

    partnerSignupMutation.mutate(formData, {
      onSuccess: () => {
        setFormData(initialFormData);
        toast({title: t('partners.sign-up.success'), status: 'success'});
      },
      onError: (e) => toast({status: 'error', title: e.response?.data.message ?? t('app.errors.try-again')}),
    });
  };

  return (
    <>
      <Head>
        <meta name="description" content="Predict game score and win prizes | Eksab" />
        <title>{`${t('partners')} | ${t('app.name')}`}</title>
      </Head>

      <Container py={py} flexDir="column" gap={[6, 10, 12, 16]} textAlign="center">
        <Flex as="section" flexDir="column" align="center" gap={[2, 3, 4]}>
          <Image src={handShakeIcon} alt="" width="120" />

          <Text as="h1" fontSize={['xl', '2xl', null, '3xl']} fontWeight="700" mt={[4, 5, 7]}>
            {t('partners.title')}
          </Text>

          <Text as="h2" fontSize={['md', 'lg', 'xl', '2xl']} fontWeight="500">
            {t('partners.subtitle')}
          </Text>
        </Flex>

        <Grid as="section" templateColumns="repeat(auto-fit, minmax(15rem, 1fr))" gap={[3, 5, 6, 8]}>
          <Step name="sign-up" icon={keyIcon} />
          <Step name="promote" icon={rocketIcon} />
          <Step name="get-users" icon={addUserIcon} />
          <Step name="cash-out" icon={moneyIcon} />
        </Grid>

        <Flex as="section" flexDir="column" gap={[4, 6, 8]}>
          <Text as="h2" fontSize={['xl', null, '2xl']} fontWeight="700">
            {t('partners.products')}
          </Text>

          <Flex align="center" justify="space-between" gap="7" flexWrap="wrap">
            <Product name="predictions" icon={tacticsScreenshot} />
            <Product name="fantasy" icon={fantasyScreenshot} />
            <Product name="trivia" icon={triviaScreenshot} />
          </Flex>
        </Flex>

        <Flex as="section" flexDir="column" align="center" gap={[4, 6, 8]}>
          <Text as="h2" fontSize="2xl" fontWeight="700">
            {t('partners.steps.sign-up.title')}
          </Text>

          <Flex as="form" flexDir="column" gap={[3, null, 4]} w="full" maxW="33.5rem" onSubmit={onSubmit}>
            <InputField
              placeholder={t('partners.sign-up.fields.name')}
              value={formData.name}
              onChange={onInputChange('name')}
            />

            <InputField
              placeholder={t('partners.sign-up.fields.email')}
              type="email"
              inputMode="email"
              value={formData.email}
              onChange={onInputChange('email')}
            />

            <InputField
              placeholder={t('partners.sign-up.fields.phone')}
              type="tel"
              inputMode="tel"
              value={formData.phone_number}
              onChange={onInputChange('phone_number')}
            />

            <Button
              type="submit"
              alignSelf="center"
              fontSize="md"
              fontWeight="700"
              w="60"
              isLoading={partnerSignupMutation.isLoading}
              isDisabled={
                partnerSignupMutation.isLoading || !formData.name || !formData.email || !formData.phone_number
              }
            >
              {t('partners.sign-up')}
            </Button>
          </Flex>
        </Flex>

        <Flex as="section" flexDir="column" align="center" gap="2">
          <Text as="h2" fontSize="md" fontWeight="500">
            {t('partners.contact-us', {
              br: (chunks) => (
                <>
                  <br />

                  <Text mt="1" fontSize="2xl" fontWeight="700">
                    {chunks}
                  </Text>
                </>
              ),
            })}
          </Text>

          <Link lang="en" href={`mailto:${SUPPORT_EMAIL}`} fontSize="md" fontWeight="700" color="#6c6c6c">
            {SUPPORT_EMAIL}
          </Link>
        </Flex>
      </Container>
    </>
  );
}

interface StepProps {
  name: 'sign-up' | 'promote' | 'get-users' | 'cash-out';
  icon: StaticImageData;
}

const Step = ({name, icon}: StepProps) => {
  const t = useTranslate();

  return (
    <Flex flexDir="column" align="center" p="6" rounded="2xl" borderWidth="1px" borderColor="gray.400">
      <Image src={icon} alt="" width="64" />

      <Text as="h3" fontSize={['xl', null, '2xl']} fontWeight="700" mt="4" mb={[2, 3, 4]}>
        {t(`partners.steps.${name}.title`)}
      </Text>

      <Text fontSize={['md', null, 'lg']} fontWeight="500" color="#6c6c6c" sx={{textWrap: 'balance'}}>
        {t(`partners.steps.${name}.description`)}
      </Text>
    </Flex>
  );
};

interface ProductProps {
  name: 'fantasy' | 'trivia' | 'predictions';
  icon: StaticImageData;
}

const Product = ({name, icon}: ProductProps) => {
  const t = useTranslate();

  return (
    <Flex flex={1} flexDir="column" align="center" gap={[3, 3.5]} minW="52">
      <Image src={icon} alt="" width="235" />

      <Text as="h3" fontSize={['md', 'lg', 'xl', '2xl']} fontWeight="500">
        {t(`app.${name}`)}
      </Text>
    </Flex>
  );
};

const InputField = (props: InputProps) => (
  <Input fontSize="md" fontWeight="500" rounded="xl" px="6" py="3.5" h="unset" {...props} />
);

export const getStaticProps: GetStaticProps = async ({locale}) => {
  setApiLocale(locale);

  const queryClient = new QueryClient();
  await queryClient.prefetchQuery([useRegionSettings.queryKey, locale], useRegionSettings.queryFn);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
};
